/**
 * @file Picks the objective for an agent import run from the files it carries.
 * `AIProvider` calls it from `launchImportRun` and `importIntoForm`.
 *
 * Three objectives, chosen in order: a PDF-only run for an intake reviewer
 * (`aiOperator` or `aiAdmin`) while `PDF_EXTRACTION_PATH` is `'pdfIntake'`
 * reads through `startPdfIntake` and never opens Mapping Review; any other run
 * with a data file goes extract → stage → apply; an email alone is staged from
 * its body. A mixed PDF-plus-CSV run takes the extract → stage → apply path.
 */
import { PDF_EXTRACTION_PATH, extensionOf } from '#types';
import type { FileAttachment } from './AIContext';

/** Objective for a dropped-file import run: extract → stage → apply into the bound form. */
const IMPORT_RUN_OBJECTIVE = [
  'A file is attached. Import the whole document into the bound form version.',
  'The bound form version already exists — do NOT create, copy, or duplicate any form; extract, stage, and apply into the bound form version only.',
  'Steps: (1) call extractFromFile on the attached file; (2) call proposeImport to stage the whole document against the bound form; (3) call applyToDocument to apply the staged import.',
  "Line items and other tabular or quantity data belong in the form's tables, landed as rows through this staged import — never copy or summarize them into a notes or other free-text field.",
  'Do not ask for confirmation before staging — the user reviews and approves the staged mapping in the Mapping Review UI during the applyToDocument approval.',
].join(' ');

/**
 * Objective for a dropped email with no data attachment: the request lives in the
 * email body, surfaced as the email's own file_id. Runs the same extract → stage
 * → apply pipeline so the body is reviewed in the Mapping Review UI like any file.
 */
const IMPORT_RUN_OBJECTIVE_BODY_ONLY = [
  "No data file is attached — the request is in the email body, addressable by the email's file_id.",
  'The bound form version already exists — do NOT create, copy, or duplicate any form.',
  "Steps: (1) call extractFromFile on the email's file_id to stage the body — any line items requested in the body are staged as a reviewable table; (2) call proposeImport to stage it against the bound form; (3) call applyToDocument to apply the staged import.",
  "Requested line items (products or materials with quantities) belong in the form's line-item table via this staged import — never copy or summarize them into a notes or other free-text field.",
  'If extraction yields no content, report that to the user and stop — do not loop on proposeImport.',
  'Do not ask for confirmation before staging — the user reviews and approves the staged mapping in the Mapping Review UI during the applyToDocument approval.',
].join(' ');

/** Objective for a PDF-only import run read through PDF intake: one `startPdfIntake` call per PDF, then one reply line and schedule review link per PDF. */
const IMPORT_RUN_OBJECTIVE_PDF = [
  'One or more PDFs are attached. Read each into the bound form version through PDF intake.',
  'The bound form version already exists — do NOT create, copy, or duplicate any form.',
  "Call startPdfIntake once per attached PDF, with that file's file_id; read every attached PDF. Do NOT call extractFromFile, proposeImport, or applyToDocument.",
  "When they return, reply with one short line per PDF: its file name, pages read, flagged cells, and any failed pages, then its review link as a markdown link [Open the schedule review](href) using that call's returned href exactly.",
  'If alreadyRead is true for a PDF, say on its line that it was already read and link that review.',
  'Then call finish.',
].join(' ');

/** Objective for an import run; `pdfsOpenInIntake` is true when the user is an intake reviewer (`aiOperator` or `aiAdmin`). */
export function objectiveForAttachments(
  attachments: FileAttachment[],
  pdfsOpenInIntake: boolean,
): string {
  const readsThroughPdfIntake =
    PDF_EXTRACTION_PATH === 'pdfIntake' &&
    pdfsOpenInIntake &&
    attachments.length > 0 &&
    attachments.every(
      (a) => a.kind === 'tabular' && extensionOf(a.filename) === 'pdf',
    );
  if (readsThroughPdfIntake) return IMPORT_RUN_OBJECTIVE_PDF;
  return attachments.some((a) => a.kind === 'tabular')
    ? IMPORT_RUN_OBJECTIVE
    : IMPORT_RUN_OBJECTIVE_BODY_ONLY;
}
